import { Link, useNavigate } from 'react-router-dom';
import { Shirt } from '../../components/Shirt';
import { ProductGallery } from '../../components/ProductGallery';
import { availableSizesForCut, findProductForColor, roomForColor, useShop } from '../ShopState';
import { ProductSpec, productSpecLine } from '../ProductSpec';
import { SizeChart } from '../SizeChart';
import { COLOR_META } from '../colors';
import { StepIndicator } from '../StepIndicator';
import { groszeToZl, formatGroszeAsZl } from '../../util/money';
import type { ShirtCut } from '../../types';

export function Product() {
  const navigate = useNavigate();
  const {
    selection,
    setSelection,
    verification,
    catalog,
    cart,
    addToCart,
    removeFromCart,
    cartTotalQty,
    cartTotalGrosze,
  } = useShop();

  if (catalog.status === 'loading') {
    return (
      <div style={loadingStyle}>Wczytywanie katalogu…</div>
    );
  }
  if (catalog.status === 'error') {
    return (
      <div style={loadingStyle}>Nie udało się załadować produktu.</div>
    );
  }
  const product = findProductForColor(catalog.products, selection.color);
  if (!product) {
    return (
      <div style={loadingStyle}>Wybrany kolor jest niedostępny.</div>
    );
  }
  // The router sends an unverified visitor back to the gate; this only narrows the type.
  if (!verification) return null;
  const sizes = availableSizesForCut(product, selection.cut);
  const specLine = productSpecLine(product);
  const description = product.description.trim();
  const priceZl = groszeToZl(product.price_grosze);
  const draftSubtotalGrosze = product.price_grosze * selection.qty;
  const colorLabel = COLOR_META[selection.color].label;
  // Capped by what this external order has left of the selected colour, not by a global quota: a
  // second shirt on one ticket has to be the other colour, and earlier orders spend the same share.
  const room = roomForColor(verification, cart, selection.color);
  const otherColorOnSale = catalog.products.some((p) => p.color !== selection.color);
  const canAdd = selection.qty > 0 && selection.qty <= room.left;
  const addToCartLabel = (() => {
    if (room.leftOverall <= 0) return 'OSIĄGNIĘTO LIMIT KOSZULEK';
    if (room.left <= 0) {
      return otherColorOnSale ? 'LIMIT W TYM KOLORZE · WYBIERZ INNY' : 'LIMIT W TYM KOLORZE';
    }
    if (selection.qty > room.left) return `MAX ${room.left} SZT W TYM KOLORZE`;
    return `DODAJ DO KOSZYKA · ${formatGroszeAsZl(draftSubtotalGrosze).replace(' zł', ' ZŁ')}`;
  })();
  const roomLine =
    `${colorLabel}: ` +
    (room.alreadyOrdered > 0
      ? `ZAMÓWIONO JUŻ ${room.alreadyOrdered} Z ${room.allowance}`
      : `MAX ${room.allowance} SZT NA TYM ZAMÓWIENIU`) +
    ` · POZOSTAŁO ${Math.max(0, room.left)}`;

  const pickCut = (c: ShirtCut) => {
    const next = availableSizesForCut(product, c);
    const fallback = next.includes(selection.size) ? selection.size : next[0] ?? 'M';
    setSelection({ cut: c, size: fallback });
  };

  const handleAddToCart = () => {
    if (!canAdd) return;
    addToCart({
      productId: product.id,
      color: selection.color,
      cut: selection.cut,
      size: selection.size,
      qty: selection.qty,
      unitPriceGrosze: product.price_grosze,
    });
    setSelection({ qty: 1 });
  };

  return (
    <div style={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 28px',
          borderBottom: '2px solid var(--shop-fg)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link
            to="/"
            style={{ display: 'flex', alignItems: 'center', gap: 16, color: 'inherit', textDecoration: 'none' }}
          >
            <img src="/logo.png" alt="" style={{ width: 40, height: 40, display: 'block' }} />
            <div className="display" style={{ fontSize: 16, letterSpacing: '.1em' }}>
              OPERACJA TENERYFA
            </div>
          </Link>
          <div
            className="mono"
            style={{ fontSize: 10, color: 'rgba(var(--shop-muted-rgb),.55)', letterSpacing: '.18em' }}
          >
            SKLEP · EDYCJA 2026
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div
            className="mono"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              border: '1.5px solid var(--shop-fg)',
              fontSize: 11,
              letterSpacing: '.14em',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: 'var(--orange)',
                display: 'inline-block',
              }}
            />
            ZAMÓWIENIE ZWERYFIKOWANE · {verification.firstName.toUpperCase()}
          </div>
          <StepIndicator current={2} />
        </div>
      </div>

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
            borderRight: '1px dashed rgba(var(--shop-muted-rgb),.2)',
            position: 'relative',
          }}
        >
          <div style={{ width: 460, height: 460 }}>
            {/* Keyed on the product: switching color swaps the whole photo set, and a
                surviving scroll offset would land the buyer mid-strip on slide three. */}
            <ProductGallery
              key={product.id}
              images={product.images}
              alt={product.name}
              fallback={<Shirt color={selection.color} cut={selection.cut} />}
            />
          </div>
        </div>

        <div style={{ padding: '40px 56px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div className="display" style={{ fontSize: 56, lineHeight: 0.95 }}>
              KOSZULKA
            </div>
            <div className="display" style={{ fontSize: 44, color: 'var(--orange)' }}>
              {priceZl} ZŁ
            </div>
          </div>

          {specLine && (
            <div
              className="mono"
              style={{
                marginTop: 12,
                fontSize: 12,
                letterSpacing: '.12em',
                color: 'rgba(var(--shop-muted-rgb),.7)',
              }}
            >
              {specLine}
            </div>
          )}
          {description && (
            <p style={{ marginTop: 8, marginBottom: 0, fontSize: 14, lineHeight: 1.5, color: 'rgba(var(--shop-muted-rgb),.75)', whiteSpace: 'pre-line' }}>
              {description}
            </p>
          )}

          <SectionLabel>KOLOR</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {catalog.products.map((p) => {
              const meta = COLOR_META[p.color];
              const active = p.color === selection.color;
              return (
                <button
                  key={p.color}
                  onClick={() => setSelection({ color: p.color })}
                  style={{
                    padding: '14px 18px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: active ? 'var(--orange)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--shop-fg)',
                    border: active ? '2px solid var(--orange)' : '2px solid var(--shop-fg)',
                  }}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      background: meta.swatch,
                      border: '1.5px solid currentColor',
                    }}
                  />
                  <span className="display" style={{ fontSize: 18 }}>
                    {meta.label}
                  </span>
                </button>
              );
            })}
          </div>

          <SectionLabel>KRÓJ</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {(['męski', 'damski'] as const).map((c) => {
              const active = c === selection.cut;
              return (
                <button
                  key={c}
                  onClick={() => pickCut(c)}
                  style={{
                    padding: '14px 0',
                    cursor: 'pointer',
                    background: active ? 'var(--orange)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--shop-fg)',
                    border: active ? '2px solid var(--orange)' : '2px solid var(--shop-fg)',
                    fontFamily: 'var(--display)',
                    fontSize: 18,
                    textTransform: 'uppercase',
                    letterSpacing: '.06em',
                  }}
                >
                  {c}
                </button>
              );
            })}
          </div>

          <SectionLabel>ROZMIAR</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${sizes.length},1fr)`, gap: 6 }}>
            {sizes.map((s) => {
              const active = s === selection.size;
              return (
                <button
                  key={s}
                  onClick={() => setSelection({ size: s })}
                  style={{
                    padding: '14px 0',
                    cursor: 'pointer',
                    background: active ? 'var(--orange)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--shop-fg)',
                    border: active ? '2px solid var(--orange)' : '2px solid var(--shop-fg)',
                    fontFamily: 'var(--display)',
                    fontSize: 18,
                  }}
                >
                  {s}
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 10 }}>
            <SizeChart cut={selection.cut} sizes={sizes} selected={selection.size} />
          </div>

          <SectionLabel>ILOŚĆ</SectionLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: -2 }}>
            <div style={{ display: 'flex', alignItems: 'center', border: '2px solid var(--shop-fg)' }}>
              <button
                onClick={() => setSelection({ qty: Math.max(1, selection.qty - 1) })}
                style={qtyBtnD}
                aria-label="Mniej"
              >
                −
              </button>
              <span className="display" style={{ fontSize: 22, minWidth: 40, textAlign: 'center' }}>
                {selection.qty}
              </span>
              <button
                onClick={() => setSelection({ qty: selection.qty + 1 })}
                style={qtyBtnD}
                aria-label="Więcej"
                disabled={selection.qty >= room.left}
              >
                +
              </button>
            </div>
            <span className="mono" style={{ fontSize: 11, color: 'rgba(var(--shop-muted-rgb),.55)' }}>
              {roomLine}
            </span>
          </div>

          <button
            className="btn full"
            style={{
              marginTop: 24,
              fontSize: 22,
              padding: '20px 24px',
              opacity: canAdd ? 1 : 0.4,
              cursor: canAdd ? 'pointer' : 'not-allowed',
            }}
            onClick={handleAddToCart}
            disabled={!canAdd}
          >
            {addToCartLabel}
          </button>

          <div
            style={{
              marginTop: 24,
              border: '2px solid var(--shop-fg)',
              padding: 18,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div className="display" style={{ fontSize: 16, letterSpacing: '.1em' }}>
                TWÓJ KOSZYK
              </div>
              <div className="mono" style={{ fontSize: 11, color: 'rgba(var(--shop-muted-rgb),.6)', letterSpacing: '.14em' }}>
                {cartTotalQty}/{verification.remaining}
              </div>
            </div>

            {cart.length === 0 ? (
              <div className="mono" style={{ fontSize: 11, color: 'rgba(var(--shop-muted-rgb),.5)', letterSpacing: '.08em' }}>
                KOSZYK JEST PUSTY · DODAJ PIERWSZĄ KOSZULKĘ
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {cart.map((row) => {
                  const meta = COLOR_META[row.color];
                  const rowSubtotal = row.unitPriceGrosze * row.qty;
                  return (
                    <div
                      key={row.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr auto auto',
                        gap: 10,
                        alignItems: 'center',
                        padding: '8px 10px',
                        border: '1px dashed rgba(var(--shop-muted-rgb),.25)',
                      }}
                    >
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          background: meta.swatch,
                          border: '1.5px solid var(--shop-fg)',
                          display: 'inline-block',
                        }}
                      />
                      <div style={{ minWidth: 0 }}>
                        <span className="mono" style={{ fontSize: 12, letterSpacing: '.06em' }}>
                          {meta.label} · {row.cut.toUpperCase()} · {row.size} × {row.qty}
                        </span>
                        <ProductSpec
                          product={catalog.products.find((p) => p.id === row.productId)}
                          fontSize={10}
                        />
                      </div>
                      <span className="mono" style={{ fontSize: 12 }}>
                        {formatGroszeAsZl(rowSubtotal).replace(' zł', ' zł')}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFromCart(row.id)}
                        aria-label="Usuń pozycję"
                        style={{
                          width: 24,
                          height: 24,
                          background: 'transparent',
                          border: '1px solid rgba(var(--shop-muted-rgb),.4)',
                          color: 'var(--shop-fg)',
                          cursor: 'pointer',
                          fontSize: 12,
                          lineHeight: 1,
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              className="btn full"
              style={{
                fontSize: 18,
                padding: '14px 20px',
                opacity: cart.length === 0 ? 0.4 : 1,
                cursor: cart.length === 0 ? 'not-allowed' : 'pointer',
              }}
              onClick={() => navigate('/sklep/checkout')}
              disabled={cart.length === 0}
            >
              PRZEJDŹ DO KASY · {formatGroszeAsZl(cartTotalGrosze).replace(' zł', ' ZŁ')} →
            </button>
          </div>
          <div style={{ display: 'none' }}>{priceZl}</div>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <div className="display" style={{ fontSize: 13, letterSpacing: '.14em', margin: '20px 0 8px' }}>
      {children}
    </div>
  );
}

const qtyBtnD = {
  width: 44,
  height: 44,
  background: 'transparent',
  border: 0,
  color: 'var(--shop-fg)',
  fontFamily: 'var(--display)',
  fontSize: 24,
  cursor: 'pointer',
} as const;

const loadingStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  opacity: 0.6,
} as const;
